import React, { useEffect, useState } from 'react';
import { onAuth, signInWithGoogle, sendMagicLink, completeMagicLinkIfPresent, signOutUser, authApiIdempotent } from '../firebase/config.js';
import { ENV } from '../config/env.js';
import { useStudyExamType } from '../hooks/useStudyExamType.js';
import { EXAM_TYPE_OPTIONS } from './clinical/clinicalOptions.js';

const API_BASE_URL = ENV.apiBaseUrl;

const AccountPage = ({ entitlements }) => {
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('');
    const [loadingPortal, setLoadingPortal] = useState(false);
    const [studyExamType, setStudyExamType] = useStudyExamType();

    useEffect(() => {
        const unsub = onAuth(setUser);
        completeMagicLinkIfPresent().catch(() => {});
        return () => unsub && unsub();
    }, []);

    const handleGoogle = async () => {
        setStatus('');
        try {
            await signInWithGoogle();
        } catch (e) {
            setStatus(e.message);
        }
    };

    const handleMagic = async (e) => {
        e.preventDefault();
        setStatus('Sending link...');
        try {
            await sendMagicLink(email);
            setStatus('Magic link sent. Check your email.');
        } catch (e2) {
            setStatus(e2.message);
        }
    };

    const handleSignOut = async () => {
        await signOutUser();
    };

    const openBillingPortal = async () => {
        try {
            setLoadingPortal(true);
            const data = await authApiIdempotent(`${API_BASE_URL}/api/stripe/create-portal-session`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ returnUrl: window.location.origin + '/account' })
            });
            window.location.href = data.url;
        } catch (e) {
            console.error('[AccountPage] openBillingPortal error:', { code: e?.code, requestId: e?.requestId, status: e?.status }, e);
            setStatus(e.message || 'Could not open billing portal');
        }
        finally { setLoadingPortal(false); }
    };

    const goToPricing = () => { window.location.href = '/pricing'; };

    const LabelRow = ({ label, children }) => (
        <div className="flex items-baseline gap-2">
            <div className="text-slate-300 font-semibold min-w-[128px]">{label}</div>
            <div className="text-slate-200">{children}</div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#0B0F1A] text-slate-200 px-4 py-10">
            <div className="mx-auto w-full max-w-2xl">
                <h1 className="text-3xl font-semibold tracking-tight mb-6">Account</h1>
                <div className="rounded-2xl border border-white/10 bg-slate-900/70 backdrop-blur p-6 shadow-2xl shadow-black/30">
                    {user ? (
                        <>
                            <div className="flex items-center gap-3">
                                <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">👤</div>
                                <div className="leading-tight">
                                    <div className="text-lg font-medium">Signed in successfully</div>
                                    {user.email && <div className="text-sm text-slate-400">{user.email}</div>}
                                </div>
                            </div>

                            <div className="mt-5 grid gap-2">
                                <LabelRow label="Plan:">{entitlements?.tier || 'free'} <button onClick={goToPricing} className="ml-2 text-xs text-teal-300 underline underline-offset-2">Change plan</button></LabelRow>
                                <LabelRow label="Interval:">{entitlements?.interval || 'month'}</LabelRow>
                                <LabelRow label="Credits:">{typeof entitlements?.creditsBalance === 'number' ? entitlements.creditsBalance : '-'} <button onClick={goToPricing} className="ml-2 text-xs text-amber-300 underline underline-offset-2">Add credits</button></LabelRow>
                                <LabelRow label="Audio minutes:">{typeof entitlements?.audioMinutesRemaining === 'number' ? entitlements.audioMinutesRemaining : '-'}</LabelRow>
                                <LabelRow label="Included games left:">{typeof entitlements?.includedGamesRemaining === 'number' ? entitlements.includedGamesRemaining : '-'}</LabelRow>
                                <LabelRow label="Included notes left:">{typeof entitlements?.includedNotesRemaining === 'number' ? entitlements.includedNotesRemaining : '-'}</LabelRow>
                                <LabelRow label="Included exams left:">{typeof entitlements?.includedExamsRemaining === 'number' ? entitlements.includedExamsRemaining : '-'}</LabelRow>
                                {entitlements?.cycleAnchor && (
                                    <LabelRow label="Next billing/reset:">{(() => { try { const a = entitlements.cycleAnchor; const d = (a && a.seconds ? new Date(a.seconds * 1000) : new Date(a)); return isNaN(d.getTime()) ? '-' : d.toLocaleString(); } catch { return '-'; } })()}</LabelRow>
                                )}
                            </div>

                            <div className="mt-6 border-t border-white/10 pt-5">
                                <label htmlFor="study-exam-type" className="block text-sm font-semibold text-slate-300">Study exam type</label>
                                <p className="text-xs text-slate-400 mt-0.5">Exams you generate are tailored to this exam.</p>
                                <select
                                    id="study-exam-type"
                                    value={studyExamType}
                                    onChange={(e) => setStudyExamType(e.target.value)}
                                    className="mt-2 w-full rounded-xl bg-slate-800/70 border border-white/10 px-3.5 py-2.5 text-sm text-slate-200"
                                >
                                    {EXAM_TYPE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="mt-6 flex flex-wrap justify-end gap-2">
                                <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-slate-200 bg-slate-800/70 ring-1 ring-white/10 hover:bg-slate-800" onClick={handleSignOut}>Sign out</button>
                                <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-teal-100 bg-teal-600 hover:bg-teal-500 disabled:opacity-70" disabled={loadingPortal} onClick={openBillingPortal}>{loadingPortal ? 'Opening…' : 'Manage billing'}</button>
                                <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-rose-100 bg-rose-600/90 hover:bg-rose-600 disabled:opacity-70" disabled={loadingPortal} onClick={openBillingPortal}>Cancel subscription</button>
                                <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white bg-gradient-to-r from-teal-600 to-emerald-600" onClick={() => (window.location.href = '/app')}>Continue</button>
                            </div>
                            <div className="mt-2 text-xs text-slate-400">If you cancel, your access remains active until the end of your current billing cycle. After that, you can renew anytime.</div>
                        </>
                    ) : (
                        <div className="grid gap-4">
                            <p className="text-sm text-slate-400">Sign in to sync your progress and access premium features</p>
                            <button className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/15" onClick={handleGoogle}>
                                <svg width="20" height="20" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                Continue with Google
                            </button>
                            <div className="text-center text-slate-400 text-xs">or</div>
                            <form onSubmit={handleMagic} className="grid gap-2">
                                <div className="flex gap-2">
                                    <input type="email" required placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 rounded-xl bg-slate-800/70 border border-white/10 px-3.5 py-2.5 text-sm text-slate-200" />
                                    <button className="rounded-xl px-4 py-2 text-sm text-white bg-gradient-to-r from-teal-600 to-emerald-600" type="submit">Send Magic Link</button>
                                </div>
                            </form>
                        </div>
                    )}
                    {status && <div className="mt-4 text-sm text-slate-300">{status}</div>}
                </div>
            </div>
        </div>
    );
};

export default AccountPage;
